import React, { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@apollo/client";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Grid,
  IconButton,
  Modal,
  Paper,
  Step,
  TextField,
  StepConnector,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  stepConnectorClasses,
  styled,
  TableContainer,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { IoChatbubblesOutline } from "react-icons/io5";
import { TbMessageShare } from "react-icons/tb";
import { v4 as uuidv4 } from "uuid";
import { useSnackbar } from "notistack";
import MaButton from "../../../../Components/UI/ma-button";
import { ProductThumb } from "../dashboard/recent-order";
import { OrderStatusBadge } from "./order-status-badge";
import { CancelOrderDialog } from "./cancel-order-dialog";
import {
  getLifecycleActionKey,
  getLifecyclePrimaryButtonTitle,
  mapSellerOrderDetailToPageDetail,
} from "./orders-mapper";
import {
  CANCEL_ORDER,
  ORDER_DELIVERED,
  ORDER_SHIPPED,
  PROCESS_ORDER,
} from "../../../../Utils/Mutations";
import { GET_SELLER_ORDER_DETAIL } from "../../../../Utils/Queries";
import socket from "../../../../Utils/socket";

const CHAT_MODAL_STYLE = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { lg: 400, md: 400, sm: 300, xs: 300 },
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  boxSizing: "border-box",
  p: 4,
};

const SOCKET_RECEIVED_MESSAGE = "receivedMessage";
const SOCKET_EXCEPTION = "exception";
const SOCKET_INITIATE_CHAT = "initiateChatWithASeller";

const PurpleConnector = styled(StepConnector)(() => ({
  [`&.${stepConnectorClasses.active}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: "#2E1834",
    },
  },
  [`&.${stepConnectorClasses.completed}`]: {
    [`& .${stepConnectorClasses.line}`]: {
      borderColor: "#2E1834",
    },
  },
  [`& .${stepConnectorClasses.line}`]: {
    borderTopWidth: 2,
    borderColor: "#E5E7EB",
  },
}));

function Money({ n }) {
  const v =
    typeof n === "number"
      ? n.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })
      : n;
  return <span className="tabular-nums">${v}</span>;
}

function DetailRow({ label, children, className = "" }) {
  return (
    <div className={`flex items-start justify-between gap-4 ${className}`}>
      <dt className="shrink-0 text-[#9CA3AF]">{label}</dt>
      <dd className="my-0 text-right font-medium text-[#374151]">{children}</dd>
    </div>
  );
}

function OrderLineItemMobile({ row, image }) {
  return (
    <div className="flex gap-3 border-b border-[#F3F4F6] pb-4 last:border-0 last:pb-0">
      <ProductThumb src={image} alt="" />
      <div className="min-w-0 flex-1">
        <p className="my-0 text-[13px] font-semibold text-[#1F2937]">
          {row.name}
        </p>
        <p className="my-0 mt-0.5 text-[11px] text-[#9CA3AF]">{row.sku}</p>
        <div className="mt-2 grid grid-cols-3 gap-2 text-[12px]">
          <div>
            <span className="text-[#9CA3AF]">Qty</span>
            <p className="my-0 font-semibold tabular-nums text-[#374151]">
              {row.qty}
            </p>
          </div>
          <div>
            <span className="text-[#9CA3AF]">Price</span>
            <p className="my-0 font-semibold text-[#374151]">{row.unitPrice}</p>
          </div>
          <div className="text-right">
            <span className="text-[#9CA3AF]">Subtotal</span>
            <p className="my-0 font-semibold text-[#1F2937]">{row.subtotal}</p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default function OrderDetailPage() {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const { data, loading, error, refetch } = useQuery(GET_SELLER_ORDER_DETAIL, {
    variables: { orderItemId: orderId ?? "" },
    skip: !orderId,
    fetchPolicy: "network-only",
  });

  const [processOrder] = useMutation(PROCESS_ORDER);
  const [cancelOrder] = useMutation(CANCEL_ORDER);
  const [shipOrder] = useMutation(ORDER_SHIPPED);
  const [deliveredOrder] = useMutation(ORDER_DELIVERED);

  const [cancelOpen, setCancelOpen] = useState(false);
  const [lifecycleLoading, setLifecycleLoading] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [openChat, setOpenChat] = useState(false);
  const [chatMessage, setChatMessage] = useState("");
  const [chatSent, setChatSent] = useState(false);
  const [chatSubmitting, setChatSubmitting] = useState(false);
  const chatTimeoutRef = useRef(null);
  const isMountedRef = useRef(true);
  const pendingChatSocketListenersRef = useRef(null);
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (chatTimeoutRef.current) {
        clearTimeout(chatTimeoutRef.current);
        chatTimeoutRef.current = null;
      }
      if (pendingChatSocketListenersRef.current && socket) {
        socket.off(
          SOCKET_EXCEPTION,
          pendingChatSocketListenersRef.current.onException,
        );
        socket.off(
          SOCKET_RECEIVED_MESSAGE,
          pendingChatSocketListenersRef.current.onReceived,
        );
        pendingChatSocketListenersRef.current = null;
      }
    };
  }, []);

  const detail = useMemo(() => {
    const item = data?.getSellerOrderDetail;
    return item ? mapSellerOrderDetailToPageDetail(item) : null;
  }, [data]);

  const actionKey = useMemo(
    () => getLifecycleActionKey(detail?.raw?.status),
    [detail?.raw?.status],
  );
  const primaryTitle = getLifecyclePrimaryButtonTitle(actionKey);

  const canChatWithSender = useMemo(() => {
    const raw = detail?.raw;
    if (!raw?.product?._id) return false;
    const s = String(raw.status ?? "")
      .trim()
      .toUpperCase();
    if (s.includes("CANCEL") || s.includes("REFUND")) return false;
    return true;
  }, [detail?.raw?.product?._id, detail?.raw?.status]);

  const handleOpenChat = () => {
    if (!canChatWithSender) {
      enqueueSnackbar(
        "Chat is unavailable for this order (product or status missing).",
        { variant: "warning" },
      );
      return;
    }
    setOpenChat(true);
  };

  const handleCloseChat = () => {
    if (chatTimeoutRef.current) {
      clearTimeout(chatTimeoutRef.current);
      chatTimeoutRef.current = null;
    }
    if (pendingChatSocketListenersRef.current && socket) {
      socket.off(
        SOCKET_EXCEPTION,
        pendingChatSocketListenersRef.current.onException,
      );
      socket.off(
        SOCKET_RECEIVED_MESSAGE,
        pendingChatSocketListenersRef.current.onReceived,
      );
      pendingChatSocketListenersRef.current = null;
    }
    setOpenChat(false);
    setChatSent(false);
    setChatMessage("");
    setChatSubmitting(false);
  };

  const handleSendChatMessage = () => {
    const productOrServiceId = detail?.raw?.product?._id;
    if (!productOrServiceId || !chatMessage.trim()) return;

    const payload = {
      productOrServiceId,
      message: chatMessage.trim(),
      clientMessageId: uuidv4(),
      operationMode: "Product",
    };

    if (!socket) {
      enqueueSnackbar("Chat is unavailable.", { variant: "error" });
      return;
    }

    setChatSubmitting(true);

    const clearChatTimeout = () => {
      if (chatTimeoutRef.current) {
        clearTimeout(chatTimeoutRef.current);
        chatTimeoutRef.current = null;
      }
    };

    let settled = false;
    const finish = () => {
      if (settled) return;
      settled = true;
      clearChatTimeout();
      socket.off(SOCKET_EXCEPTION, onException);
      socket.off(SOCKET_RECEIVED_MESSAGE, onReceived);
      pendingChatSocketListenersRef.current = null;
      if (isMountedRef.current) {
        setChatSubmitting(false);
      }
    };

    const onException = (data) => {
      finish();
      if (isMountedRef.current) {
        enqueueSnackbar(data?.message ?? "Something went wrong", {
          variant: "error",
        });
      }
    };

    const onReceived = () => {
      finish();
      if (isMountedRef.current) {
        setChatSent(true);
      }
    };

    chatTimeoutRef.current = setTimeout(() => {
      if (!settled) {
        finish();
        if (isMountedRef.current) {
          enqueueSnackbar("Request timed out. Please try again.", {
            variant: "warning",
          });
        }
      }
    }, 20000);

    socket.on(SOCKET_EXCEPTION, onException);
    socket.on(SOCKET_RECEIVED_MESSAGE, onReceived);
    pendingChatSocketListenersRef.current = { onException, onReceived };

    try {
      socket.emit(SOCKET_INITIATE_CHAT, payload);
    } catch (err) {
      finish();
      if (isMountedRef.current) {
        enqueueSnackbar(err?.message ?? "Something went wrong", {
          variant: "error",
        });
      }
    }
  };

  const handlePrimaryAction = async () => {
    if (!actionKey || !detail?.id) return;
    setLifecycleLoading(true);
    const orderIds = String(detail.id);
    try {
      if (actionKey === "accept") {
        await processOrder({ variables: { orderIds } });
      } else if (actionKey === "ship") {
        await shipOrder({ variables: { orderIds } });
      } else {
        await deliveredOrder({ variables: { orderIds } });
      }
      await refetch();
      enqueueSnackbar("Order successfully updated", { variant: "success" });
    } catch (err) {
      enqueueSnackbar(err?.message ?? "Something went wrong", {
        variant: "error",
      });
    } finally {
      setLifecycleLoading(false);
    }
  };

  const handleCancelOrder = async ({ reason, comments }) => {
    if (!detail?.id) return;

    const cleanReason = String(reason ?? "").trim();
    const cleanDescription = String(comments ?? "").trim();

    if (!cleanReason) {
      enqueueSnackbar("Select a cancellation reason.", { variant: "warning" });
      throw new Error("Select a cancellation reason.");
    }

    setCancelLoading(true);
    try {
      const response = await cancelOrder({
        variables: {
          itemId: String(detail.id),
          reason: cleanReason,
          description: cleanDescription || cleanReason,
        },
      });
      await refetch();
      enqueueSnackbar(
        response?.data?.cancelProductOrderByBuyer?.message ??
          "Order cancelled successfully.",
        { variant: "success" },
      );
      setCancelOpen(false);
    } catch (err) {
      enqueueSnackbar(err?.message ?? "Could not cancel order.", {
        variant: "error",
      });
      throw err;
    } finally {
      setCancelLoading(false);
    }
  };

  if (loading && !detail) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center gap-3 px-4">
        <p className="text-[14px] text-[#6B7280]">Loading order…</p>
        <Link
          to="/seller/orders"
          className="text-[14px] font-semibold text-[#2E1834]"
        >
          Back to orders
        </Link>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className="flex min-h-[40vh] flex-col items-center justify-center px-4">
        <p className="text-[15px] text-[#6B7280]">Order not found.</p>
        <Link
          to="/seller/orders"
          className="mt-4 text-[14px] font-semibold text-[#2E1834]"
        >
          Back to orders
        </Link>
      </div>
    );
  }

  const { payment, lineItems, stepLabels, activeStep } = detail;
  const productImage = detail.raw?.product?.images?.[0];

  const paymentDetailsBlock = (
    <div className="space-y-3 text-[13px]">
      <DetailRow label="Subtotal">
        <Money n={payment.subtotal} />
      </DetailRow>
      <DetailRow label="Discount">
        <Money n={payment.discount} />
      </DetailRow>
      <DetailRow label="Tax">
        <Money n={payment.tax} />
      </DetailRow>
      <DetailRow label={`Platform Fee (${payment.platformPct}%)`}>
        <Money n={payment.platformFee} />
      </DetailRow>
      <div className="flex items-end justify-between gap-4 border-t border-[#F0F0F0] pt-4">
        <span className="text-[14px] font-bold text-[#18181B] sm:text-[15px]">
          Total Amount
        </span>
        <span className="text-[20px] font-bold tabular-nums text-[#18181B] sm:text-[22px]">
          <Money n={payment.total} />
        </span>
      </div>
      <p className="my-0 text-right text-[12px] font-medium text-[#9CA3AF]">
        You will receive
      </p>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#F9FAFB] pb-16 pt-4 md:pt-8">
      <div className="mx-auto max-w-[1200px] px-4 md:px-6">
        {/* Header */}
        <div className="mb-5 flex flex-col gap-4 md:mb-8 lg:flex-row lg:items-start lg:justify-between">
          <div className="min-w-0">
            <p
              onClick={() => navigate("/seller/orders")}
              className="mb-2 inline-flex cursor-pointer items-center gap-1 text-[13px] font-semibold text-[#6B7280] hover:text-[#374151] md:mb-3"
            >
              ← Orders
            </p>
            <h1 className="my-0 text-[20px] font-semibold tracking-tight text-[#0F0F0F] sm:text-[22px] md:text-[24px]">
              Order Details
            </h1>
            <p className="my-0 mt-1 text-[12px] text-[#6B7280] sm:text-[13px]">
              Order Placed on {detail.datePlacedLabel}
            </p>
          </div>
          <div className="grid grid-cols-1 gap-2 sm:grid-cols-3 lg:flex lg:flex-wrap lg:justify-end [&>div]:w-full lg:[&>div]:w-auto">
            <MaButton
              variant="primary"
              title={primaryTitle}
              disabled={!actionKey || lifecycleLoading}
              onClick={handlePrimaryAction}
              sx={{
                bgcolor: "#2E1834",
                minHeight: "44px",
                borderRadius: "12px",
                px: 2.5,
                fontWeight: 600,
                width: { xs: "100%", lg: "auto" },
              }}
            />
            <MaButton
              title="Cancel Order"
              onClick={() => setCancelOpen(true)}
              disabled={cancelLoading || lifecycleLoading}
              sx={{
                bgcolor: "#FFFFFF",
                color: "#DC2626",
                border: "1px solid #FECACA",
                minHeight: "44px",
                borderRadius: "12px",
                fontWeight: 600,
                width: { xs: "100%", lg: "auto" },
                "&:hover": { bgcolor: "#FEF2F2" },
              }}
            />
            <MaButton
              title="Print Order"
              onClick={() => window.print()}
              sx={{
                bgcolor: "#FFFFFF",
                color: "#18181B",
                border: "1px solid #E5E7EB",
                minHeight: "44px",
                borderRadius: "12px",
                fontWeight: 600,
                width: { xs: "100%", lg: "auto" },
                "&:hover": { bgcolor: "#F9FAFB" },
              }}
            />
          </div>
        </div>

        {detail.cancellationReason && (
          <Alert severity="error" className="mb-6 rounded-xl">
            Reason for cancellation: {detail.cancellationReason}
          </Alert>
        )}

        {/* Stepper */}
        <Paper
          elevation={0}
          className="mb-4 overflow-x-auto rounded-2xl border border-[#EFEFEF] px-2 py-5 sm:px-4 sm:py-6 md:mb-6 md:px-8"
          sx={{ bgcolor: "#FFFFFF" }}
        >
          <Stepper
            activeStep={activeStep}
            alternativeLabel
            connector={<PurpleConnector />}
            sx={{
              minWidth: { xs: 520, md: "auto" },
              px: { xs: 0.5, sm: 1 },
            }}
          >
            {stepLabels.map((label) => (
              <Step key={label}>
                <StepLabel
                  sx={{
                    "& .MuiStepLabel-label": {
                      fontSize: { xs: "9px", sm: "11px", md: "12px" },
                      fontWeight: 600,
                      mt: 1,
                      whiteSpace: { xs: "normal", md: "nowrap" },
                    },
                    "& .MuiStepLabel-label.Mui-active": {
                      color: "#2E1834",
                    },
                    "& .MuiStepLabel-label.Mui-completed": {
                      color: "#374151",
                    },
                    "& .MuiSvgIcon-root": {
                      color: "#E5E7EB",
                    },
                    "& .MuiSvgIcon-root.Mui-active": {
                      color: "#2E1834",
                    },
                    "& .MuiSvgIcon-root.Mui-completed": {
                      color: "#2E1834",
                    },
                  }}
                >
                  {label}
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>

        <Grid container spacing={3}>
          {/* Sidebar: order + customer — first on mobile */}
          <Grid item xs={12} lg={4} sx={{ order: { xs: 1, lg: 2 } }}>
            <div className="flex flex-col gap-4">
              <Paper
                elevation={0}
                className="rounded-2xl border border-[#EFEFEF] p-4 sm:p-5 md:p-6"
                sx={{ bgcolor: "#FFFFFF" }}
              >
                <p className="mb-4 my-0 text-[15px] font-bold text-[#18181B]">
                  Order Details
                </p>
                <dl className="space-y-3 text-[13px]">
                  <DetailRow label="Order ID">
                    <span className="font-semibold text-[#1F2937]">
                      {detail.orderRef}
                    </span>
                  </DetailRow>
                  <DetailRow label="Date of Purchase">
                    {detail.dateShortLabel}
                  </DetailRow>
                  <DetailRow label="Amount">
                    <span className="font-semibold text-[#1F2937]">
                      {detail.headlineAmount}
                    </span>
                  </DetailRow>
                  <div className="flex items-center justify-between gap-4">
                    <dt className="text-[#9CA3AF]">Status</dt>
                    <dd className="my-0">
                      <OrderStatusBadge status={detail.status} />
                    </dd>
                  </div>
                </dl>
              </Paper>

              <Paper
                elevation={0}
                className="rounded-2xl border border-[#EFEFEF] p-4 sm:p-5 md:p-6"
                sx={{ bgcolor: "#FFFFFF" }}
              >
                <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                  <p className="text-[15px] my-0 font-bold text-[#18181B]">
                    Customer Details
                  </p>
                  <button
                    type="button"
                    disabled={!canChatWithSender}
                    onClick={handleOpenChat}
                    title={
                      canChatWithSender
                        ? "Message the buyer about this order"
                        : "Chat unavailable for cancelled orders or when product data is missing"
                    }
                    className="flex w-full shrink-0 items-center justify-center gap-1.5 rounded-lg border border-[#E5E7EB] bg-[#F8F8F8] px-3 py-2 text-[12px] font-semibold text-[#374151] transition-colors hover:bg-[#F3F4F6] disabled:cursor-not-allowed disabled:opacity-50 sm:w-auto"
                  >
                    <IoChatbubblesOutline className="text-[16px]" />
                    Send message
                  </button>
                </div>
                <dl className="space-y-3 text-[13px]">
                  <div>
                    <dt className="text-[#9CA3AF]">Name</dt>
                    <dd className="my-0 mt-0.5 font-semibold text-[#2563EB] underline decoration-[#2563EB]/30 underline-offset-2">
                      {detail.customer.name}
                    </dd>
                  </div>
                  <div className="hidden sm:block">
                    <dt className="text-[#9CA3AF]">Email</dt>
                    <dd className="my-0 mt-0.5 font-medium text-[#374151]">
                      {detail.customer.email}
                    </dd>
                  </div>
                  <div className="hidden sm:block">
                    <dt className="text-[#9CA3AF]">Phone</dt>
                    <dd className="my-0 mt-0.5 font-medium text-[#374151]">
                      {detail.customer.phone}
                    </dd>
                  </div>
                  <DetailRow label="Location">
                    {detail.customer.location}
                  </DetailRow>
                  <DetailRow label="Delivery Type">
                    {detail.customer.deliveryType}
                  </DetailRow>
                  <div className="hidden sm:block">
                    <dt className="text-[#9CA3AF]">Shipping Address</dt>
                    <dd className="my-0 mt-0.5 leading-snug font-medium text-[#374151]">
                      {detail.customer.address}
                    </dd>
                  </div>
                </dl>
              </Paper>
            </div>
          </Grid>

          {/* Main: payment + summary */}
          <Grid item xs={12} lg={8} sx={{ order: { xs: 2, lg: 1 } }}>
            <div className="flex flex-col gap-4">
              <Paper
                elevation={0}
                className="order-1 rounded-2xl border border-[#EFEFEF] p-4 sm:p-5 md:order-2 md:p-6 lg:order-2"
                sx={{ bgcolor: "#FFFFFF" }}
              >
                <p className="mb-4 text-[15px] my-0 font-bold text-[#18181B]">
                  Payment Details
                </p>
                {paymentDetailsBlock}
              </Paper>

              <Paper
                elevation={0}
                className="order-2 overflow-hidden rounded-2xl border border-[#EFEFEF] md:order-1 lg:order-1"
                sx={{ bgcolor: "#FFFFFF" }}
              >
                <Box sx={{ px: { xs: 2, sm: 3 }, py: 3 }}>
                  <p className="mb-4 text-[15px] my-0 font-bold text-[#3D3D3D]">
                    Order Summary
                  </p>

                  <TableContainer>
                    <Table
                      size="small"
                      className="hidden md:table"
                      sx={{ mt: 2 }}
                    >
                      <TableHead>
                        <TableRow
                          sx={{
                            bgcolor: "#F8F8F8",
                            "& th": {
                              border: "none",
                              py: 1.5,
                              fontSize: "11px",
                              fontWeight: 700,
                              color: "#9CA3AF",
                              letterSpacing: "0.06em",
                            },
                          }}
                        >
                          <TableCell>PRODUCT</TableCell>
                          <TableCell align="center">QUANTITY</TableCell>
                          <TableCell align="right">PRICE</TableCell>
                          <TableCell align="right">SUBTOTAL</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {lineItems.map((row) => (
                          <TableRow
                            key={row.id}
                            sx={{ "& td": { borderColor: "#F3F4F6", py: 2 } }}
                          >
                            <TableCell>
                              <div className="flex items-center gap-3">
                                <ProductThumb src={productImage} alt="" />
                                <div>
                                  <p className="my-0 text-[13px] font-semibold text-[#1F2937]">
                                    {row.name}
                                  </p>
                                  <p className="my-0 text-[11px] text-[#9CA3AF]">
                                    {row.sku}
                                  </p>
                                </div>
                              </div>
                            </TableCell>
                            <TableCell align="center" className="tabular-nums">
                              {row.qty}
                            </TableCell>
                            <TableCell align="right" className="font-medium">
                              {row.unitPrice}
                            </TableCell>
                            <TableCell align="right" className="font-semibold">
                              {row.subtotal}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              </Paper>
            </div>
          </Grid>
        </Grid>
      </div>

      <Modal
        open={openChat}
        onClose={handleCloseChat}
        aria-labelledby="seller-order-chat-title"
        aria-describedby="seller-order-chat-description"
      >
        <Box sx={CHAT_MODAL_STYLE}>
          {chatSent ? (
            <Box sx={{ textAlign: "center", p: 3 }}>
              <TbMessageShare />
              <Typography sx={{ color: "#333", fontWeight: 700 }}>
                Message Sent
              </Typography>
              <Button
                variant="contained"
                onClick={handleCloseChat}
                sx={{ mt: 3, borderRadius: "100px", px: 5 }}
              >
                Done
              </Button>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  id="seller-order-chat-title"
                  sx={{ color: "#333", fontWeight: 700 }}
                >
                  <IoChatbubblesOutline
                    style={{ verticalAlign: "middle", marginRight: 6 }}
                  />
                  Chat with sender
                </Typography>
                <IconButton onClick={handleCloseChat} aria-label="Close chat">
                  <ClearIcon sx={{ color: "#333" }} />
                </IconButton>
              </Box>
              <Typography
                id="seller-order-chat-description"
                sx={{
                  mt: 2,
                  color: "#333",
                  lineHeight: "145%",
                  fontSize: "12px",
                  fontWeight: 400,
                }}
              >
                Connect with your buyer: manage orders and address inquiries
                seamlessly.
              </Typography>
              <Box sx={{ mt: 1 }}>
                <TextField
                  fullWidth
                  multiline
                  value={chatMessage}
                  onChange={(e) => setChatMessage(e.target.value)}
                  minRows={2}
                  maxRows={6}
                  margin="normal"
                  placeholder="Your message here..."
                  disabled={chatSubmitting}
                  InputProps={{
                    style: {
                      borderRadius: "10px",
                      background: "#fff",
                      fontWeight: 500,
                      fontSize: "12px",
                      color: "#333",
                    },
                  }}
                />
              </Box>
              <Box
                sx={{
                  mt: 2,
                  display: "flex",
                  justifyContent: "space-between",
                  columnGap: 3,
                }}
              >
                <Button
                  fullWidth
                  sx={{
                    border: "1px solid #E6E6E6",
                    color: "#333",
                    borderRadius: "100px",
                  }}
                  onClick={handleCloseChat}
                  disabled={chatSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  sx={{ borderRadius: "100px" }}
                  fullWidth
                  color="error"
                  onClick={handleSendChatMessage}
                  variant="contained"
                  disabled={!chatMessage.trim() || chatSubmitting}
                >
                  {chatSubmitting ? "Sending…" : "Send Message"}
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Modal>

      <CancelOrderDialog
        open={cancelOpen}
        onClose={() => {
          if (!cancelLoading) setCancelOpen(false);
        }}
        onConfirm={handleCancelOrder}
        loading={cancelLoading}
      />
    </div>
  );
}
